import { useState } from "react";
import type { DecodedField, DecodedLine } from "../api";
import { fieldLabel, formatFieldValue } from "../format";

// The record console — where a 128-character fixed-width line becomes readable.
//
// A Betalingsservice record is defined by character POSITIONS: "the amount is
// positions 60–72". So the raw text is laid out under a position ruler on the
// same 1ch grid, each field is a span you can hover, and clicking a line opens
// the layout it was read with. Nothing is reformatted — these are the file's own
// characters, in their own columns. The gutter stays pinned while you scroll
// right, so you never lose track of which record you are reading.

/** The position scale: each multiple of ten, right-aligned at that column. */
function scaleRow(width: number): string {
  const columns = new Array<string>(width).fill(" ");
  for (let position = 10; position <= width; position += 10) {
    const label = String(position);
    for (let index = 0; index < label.length; index += 1) {
      columns[position - label.length + index] = label[index] ?? " ";
    }
  }
  return columns.join("");
}

/** A mark at every tenth column, a lighter one at every fifth. */
function tickRow(width: number): string {
  let ticks = "";
  for (let position = 1; position <= width; position += 1) {
    ticks += position % 10 === 0 ? "│" : position % 5 === 0 ? "·" : " ";
  }
  return ticks;
}

function Ruler({ width }: { width: number }): JSX.Element {
  return (
    <div className="ruler">
      <div className="record-row">
        <span className="gutter" />
        <span className="line">{scaleRow(width)}</span>
      </div>
      <div className="record-row">
        <span className="gutter">position</span>
        <span className="line">{tickRow(width)}</span>
      </div>
    </div>
  );
}

function FieldTable({ fields }: { fields: DecodedField[] }): JSX.Element {
  return (
    <div className="field-table">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th className="right">Positions</th>
              <th>Value</th>
              <th className="desc">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.name} data-filler={field.filler}>
                <td>{fieldLabel(field.name)}</td>
                <td className="right">
                  {field.start + 1}–{field.start + field.width}
                </td>
                <td>{field.filler ? "—" : formatFieldValue(field.name, field.value)}</td>
                <td className="desc">{field.filler ? "Padding." : meaningOf(field)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function meaningOf(field: DecodedField): string {
  if (field.description) return field.description;
  return field.constValue !== undefined ? `Always ${field.constValue}.` : "";
}

function Record({ line }: { line: DecodedLine }): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasFields = line.fields.length > 0;
  const marker = `${String(line.lineNumber).padStart(3, " ")} ${line.recordType ?? "···"}`;

  return (
    <>
      <button
        type="button"
        className="record record-row"
        aria-expanded={hasFields ? open : undefined}
        onClick={() => hasFields && setOpen(!open)}
        title={hasFields ? `${line.label ?? ""} — show the record layout` : undefined}
      >
        <span className="gutter">
          <span className="marker">{marker}</span>
          <span className="record-label">{line.label ?? ""}</span>
        </span>
        <span className="line">
          {hasFields ? (
            line.fields.map((field, index) => (
              <span
                key={field.name}
                className="field"
                data-filler={field.filler}
                data-alt={index % 2 === 1}
                title={fieldTooltip(field)}
              >
                {field.raw}
              </span>
            ))
          ) : (
            <span className="undecoded">{line.raw}</span>
          )}
        </span>
      </button>
      {open ? <FieldTable fields={line.fields} /> : null}
    </>
  );
}

function fieldTooltip(field: DecodedField): string {
  const positions = `${field.start + 1}–${field.start + field.width}`;
  const value = field.filler ? "padding" : formatFieldValue(field.name, field.value);
  return `${fieldLabel(field.name)} · ${positions} · ${value}`;
}

export function RecordConsole({ lines, width }: { lines: DecodedLine[]; width: number }): JSX.Element {
  const annotated = lines.some((line) => line.fields.length > 0);
  return (
    <div className="console">
      <div className="console-inner">
        {annotated ? <Ruler width={width} /> : null}
        {lines.map((line) => (
          <Record key={line.lineNumber} line={line} />
        ))}
      </div>
    </div>
  );
}
